import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactSection from "@/components/ContactSection";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { Sprout, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import bannerImage from "@/assets/terriccio.jpg";

const ConcimiTerricci = () => {
  const products = [
    {
      name: "Concimi organici",
      description: "Fertilizzanti naturali per nutrire il terreno in modo sostenibile",
      features: ["Stallatico pellettato", "Stallatico fino a terriccio"]
    },
    {
      name: "Concimi chimici",
      description: "Fertilizzanti minerali per un nutrimento mirato ed efficace",
      features: ["NPK completi", "Concimi granulari", "Prodotti idrosolubili", "Formulazioni specifiche"]
    },
    {
      name: "Terricci e substrati",
      description: "Terricci di qualità per orto, giardino e piante ornamentali",
      features: ["Terriccio universale", "Substrati per orto", "Substrato per prato", "Terricci per acidofile", "Terricci per agrumi", "Terricci per piante grasse", "Terricci per bonsai", "Terricci per orchidee", "Terricci per semina"]
    },
    {
      name: "Ammendanti",
      description: "Prodotti per migliorare la struttura e la fertilità del terreno",
      features: ["Torba bionda e nera", "Argilla espansa", "Lapillo", "Corteccia decorativa"]
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Concimi e Terricci Magione - Biologici e Chimici"
        description="Concimi organici e chimici, terricci per orto e giardino, ammendanti e substrati. Stallatico, NPK, torba, argilla espansa. A Magione (PG)."
        canonical="/prodotti/concimi-terricci"
        type="product"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Concimi e Terricci", url: "/prodotti/concimi-terricci" }
      ]} />
      <Navbar />

      {/* Hero Banner Section */}
      <section className="relative h-[450px] md:h-[500px] overflow-hidden pt-24 md:pt-28">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bannerImage})` }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

        {/* Content */}
        <div className="relative container mx-auto px-4 h-full flex flex-col justify-start md:justify-center pt-2 md:pt-0">
          <Link to="/#prodotti" className="mb-6 w-fit">
            <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 backdrop-blur-sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai prodotti
            </Button>
          </Link>

          <div className="max-w-4xl">
            <div className="icon-wrapper mb-6 w-16 h-16 text-white hidden md:flex items-center justify-center">
              <Sprout className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-white">
              Concimi e Terricci
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Prodotti per la nutrizione delle piante e il miglioramento del terreno.
              Soluzioni organiche e chimiche per orto, giardino e agricoltura.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {products.map((product, index) => (
              <div
                key={index}
                className={`bg-card rounded-2xl p-8 border border-border card-hover ${
                  product.name === "Terricci e substrati" ? "lg:col-span-2" : ""
                }`}
              >
                <h3 className={`font-heading font-semibold mb-4 ${
                  product.name === "Terricci e substrati" ? "text-2xl" : "text-xl"
                }`}>
                  {product.name}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {product.description}
                </p>
                <ul className={`space-y-2 ${product.name === "Terricci e substrati" ? "grid grid-cols-2 gap-x-4 gap-y-2" : ""}`}>
                  {product.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <span className="text-primary mr-2 flex-shrink-0">✓</span>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold mb-2 text-green-900 dark:text-green-100">
              🌱 Prodotti biologici disponibili
            </h3>
            <p className="text-green-800 dark:text-green-200">
              Disponiamo di un'ampia gamma di concimi e ammendanti certificati per agricoltura biologica,
              ideali per chi vuole coltivare in modo naturale e sostenibile.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-heading font-bold mb-4">
            Hai bisogno di consigli?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            I nostri esperti possono aiutarti a scegliere i prodotti più adatti
            alle tue colture e al tipo di terreno.
          </p>
          <Link
            to="/#contatti"
            className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            Contattaci
          </Link>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default ConcimiTerricci;
